/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Flex, Text, VStack } from '@chakra-ui/react';
import { ParsedParams } from '@karya/generic-task-react';
import { PlatformLocaleTextComponent } from '@karya/core';
import { WarningIcon } from '@chakra-ui/icons';
import { ColorMap } from '@/themes/Attributes';

/**
 * @param localizedText - Object containing localized text
 * @returns
 * DEFAULT text if available, otherwise EN text, or the first available localization.
 * If no valid text is found, returns null.
 */
function getStringFromLocalizedText(localizedText: Record<string, string>): string | null {
  if (typeof localizedText !== 'object') {
    return null;
  }
  if (localizedText.DEFAULT) {
    return localizedText.DEFAULT;
  } else if (localizedText.EN) {
    return localizedText.EN;
  } else {
    const firstLocalization = Object.values(localizedText)[0];
    if (firstLocalization) {
      return firstLocalization;
    }
  }
  return null;
}

export function PlatformLocaleTextRenderer({ params }: { params: ParsedParams<any> }) {
  const typedParams = params as ParsedParams<PlatformLocaleTextComponent>;
  const text = getStringFromLocalizedText(typedParams.text);

  if (text === null) {
    return (
      <VStack>
        <Flex>
          <WarningIcon />
          <Text fontSize="xs" ml={2} color={ColorMap.crimsonRed}>
            Warning: Empty or Invalid Localizations provided
          </Text>
        </Flex>
        <Text fontSize="sm" color={ColorMap.crimsonRed}>
          {JSON.stringify(typedParams.text)}
        </Text>
      </VStack>
    );
  }
  return <Text fontSize="sm">{text}</Text>;
}
